import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { DocumentCategoryInstance } from "@/stores/admin/models";

interface CreateFilterGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: DocumentCategoryInstance[];
}

export const CreateFilterGroupDialog = observer(
  ({ open, onOpenChange, categories }: CreateFilterGroupDialogProps) => {
    const { documentCategoriesStore } = useStores();
    const [label, setLabel] = useState("");
    const [labelEt, setLabelEt] = useState("");
    const [linkedCategoryId, setLinkedCategoryId] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!label.trim()) {
        toast.error("Label is required");
        return;
      }

      setIsSubmitting(true);
      try {
        await documentCategoriesStore.createFilterGroup(
          label.trim(),
          labelEt.trim() || null,
          linkedCategoryId
        );
        toast.success("Filter group created successfully");
        handleClose();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to create filter group");
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleClose = () => {
      setLabel("");
      setLabelEt("");
      setLinkedCategoryId(null);
      onOpenChange(false);
    };

    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Filter Group</DialogTitle>
            <DialogDescription>
              Create a new filter group to organize categories in the document filter UI. Optionally
              link it to an existing category.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="label">Label *</Label>
                <Input
                  id="label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Design Documentation"
                  autoFocus
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="label_et">Label (ET)</Label>
                <Input
                  id="label_et"
                  value={labelEt}
                  onChange={(e) => setLabelEt(e.target.value)}
                  placeholder="e.g., Projektdokumentatsioon"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="linkedCategory">Linked Category (optional)</Label>
                <Select
                  value={linkedCategoryId?.toString() ?? "none"}
                  onValueChange={(value) =>
                    setLinkedCategoryId(value === "none" ? null : parseInt(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No linked category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked category</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.category_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  If linked, clicking this group in the filter UI will also match documents with the
                  linked category.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !label.trim()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }
);
